/**
 * Which plan tier a build grants — #477.
 *
 * Replaces #460's `grantedForBuild(MODE !== 'production')`, which was a
 * boolean: every non-production build granted every entitlement, all seven,
 * unconditionally. That gave developers every gated surface but also
 * removed the client's only enforcement of the one-manual-account cap
 * (`isOverFreeCap` in `useAccounts.ts`) — confirmed from the backend source,
 * there is no server-side cap, no `402`, no entitlement concept there at
 * all.
 *
 * **Whether a production build is deployed is a property of the workflows,
 * not of this file.** `.github/workflows/deploy-prod.yml` is the only thing that
 * ships `build:prod`; read it, not this paragraph, for whether and how. Staging is the one running real Plaid credentials
 * against the dev database — `config/backendOverride.ts`'s `BACKENDS`
 * docblock: "Plaid PRODUCTION credentials over the dev database." That, not
 * an analogy to production, is why staging grants `'free'`: it is the one live build where an unenforced cap
 * would act on real bank connections.
 *
 * **This is the backend's tenant, not the client's own `VITE_PLAID_ENV`.**
 * `.env.staging` sets `VITE_PLAID_ENV=production` (#479), so the client flag
 * and the backend's tenant now agree. `config/env.ts` parses it into
 * `Env['plaidEnv']`, but no code in this repo reads that field today — it is
 * a declared, unconsumed client-side flag (Plaid Link takes its environment
 * from the server-issued link token). The claim above is about which Plaid
 * *account* the staging backend calls out to server-side (production, per
 * `BACKENDS`); the flag merely mirrors it.
 *
 * A tier is the right shape because a real subscriber holds a **plan**,
 * never an arbitrary subset of seven booleans — `entitlementsForTier` maps a
 * tier to its granted set. That is the BUILD's grant: #102 (billing) does not
 * change the tier, it adds a signed-in user's RevenueCat entitlements on top
 * of it (`BillingProvider`).
 *
 * **The MODE strings are the real ones**, read from `package.json`'s build
 * scripts and the `.env.*` files, not assumed — #460's whole defect was an
 * unchecked build-flag assumption:
 *   - `npm run dev` (the vite dev server) passes no `--mode`, so its MODE is
 *     vite's own default, `'development'`.
 *   - `build:dev` passes `--mode development` explicitly — same string, so
 *     the dev server and the deployed dev build share one row below and
 *     #460's promise (every gated surface reachable on app-dev) holds.
 *   - `build:stag` passes `--mode staging`. Staging now grants `'free'` — see
 *     the Plaid reasoning above — so M5-R31 (the one-account cap) and every
 *     other paywall are exercised there, not bypassed.
 *   - `build:prod` passes `--mode production`. What deploys it is
 *     `deploy-prod.yml`, which checks this tier on the built `dist/` first. Grants `'free'`
 *     as the baseline; a real subscription (#102) is added on top by
 *     `BillingProvider`, not by changing this row.
 *
 * **An unrecognised mode fails CLOSED, to `'free'`.** #474 named the call
 * site this replaced (`MODE !== 'production'`) as failing OPEN: a mistyped
 * `--mode`, an unlisted CI mode name, or anything else not in `MODE_TIERS`
 * below would have granted every entitlement in a bundle the operator
 * believes is locked. This function cannot make a caller pass a real mode,
 * but it can refuse to reward one that isn't — the fallback is `'free'`, the
 * least-privileged tier, not `'premium'`. The build-time assertion in
 * `config/buildModes.ts` (`assertKnownMode`), called from `vite.config.ts`'s
 * `requireEnv`, rejects an unknown mode before this function is ever
 * reached at all — a stronger, separate guard. This function is the second
 * layer, for anything that ever calls `tierForBuild` with a mode that guard
 * did not see (there is none today, but the fallback costs nothing to keep).
 */

export type Tier = 'free' | 'premium'

/**
 * Compile-time pin: three call sites treat `Tier` as having exactly these
 * two members rather than re-deriving it — `Developer.tsx`'s `otherTier`
 * (`TIER === 'premium' ? 'free' : 'premium'`), its N7 fix that hides "Use
 * `<build tier>`" whenever any override is active (the other tier a `Use`
 * button would offer is then always `BUILD_TIER`, which is only true because
 * there are exactly two), and `config/tierOverride.ts`'s `TIERS` allow-list
 * (`readonly Tier[]`, an annotation that does not itself require
 * exhaustiveness). If a third tier is ever added, `Exclude` below stops
 * resolving to `never` and this assignment fails to compile, rather than
 * those three assumptions silently going stale.
 */
type TierIsBinary = Exclude<Tier, 'free' | 'premium'> extends never ? true : never
export const TIER_IS_BINARY: TierIsBinary = true

const MODE_TIERS: Readonly<Record<string, Tier>> = {
  development: 'premium',
  staging: 'free',
  production: 'free',
}

/**
 * Whether `mode` has its OWN entry in `MODE_TIERS` — i.e. whether
 * `tierForBuild` grants it a tier by lookup rather than by falling through
 * to the `'free'` default. `tierForBuild` below is the primary caller;
 * exported because it is also this file's own witness, in the test beside
 * it, that every mode `config/buildModes.ts`'s `KNOWN_MODES` allows through
 * the build-time gate has a row here too — the two lists exist separately on
 * purpose (rejecting an unknown mode outright is a different response from
 * granting it the least-privileged tier), but nothing else stops them
 * drifting if a fourth mode is ever added to only one.
 */
export function hasDeclaredTier(mode: string): boolean {
  // Object.hasOwn, not `mode in MODE_TIERS` or a `!== undefined` check on the
  // lookup — see tierForBuild's identical reasoning just below: a plain
  // object literal inherits from Object.prototype, so a mode string naming
  // one of ITS properties would otherwise read as declared when it isn't.
  return Object.hasOwn(MODE_TIERS, mode)
}

export function tierForBuild(mode: string): Tier {
  // `Object.hasOwn`, not `MODE_TIERS[mode] ?? 'free'` — a plain object
  // literal still inherits from `Object.prototype`, so a mode string that
  // happens to name one of ITS properties (`'constructor'`, `'toString'`,
  // …) would resolve to that inherited value. The `??` fallback only fires
  // on `undefined`/`null`, and an inherited property is neither, so that
  // form has a hole in the fail-closed guarantee for exactly this input
  // class. `hasOwn` makes every mode this object does not declare resolve
  // to `'free'`, with no exceptions.
  if (hasDeclaredTier(mode)) {
    const tier = MODE_TIERS[mode]
    if (tier !== undefined) return tier
  }
  return 'free'
}
